/**
 * Commerce Loaders — data fetchers registered for CMS block resolution.
 *
 * Standard VTEX loaders come from createVtexCommerceLoaders().
 * Auto-generated pass-throughs come from loaders.gen.ts.
 * This file only contains entries that need custom wiring
 * (cookie injection, secrets, serialization, etc.).
 */
import { getVtexConfig } from "@decocms/apps-vtex";
import { legacySuggestions } from "@decocms/apps-vtex/loaders/legacy";
import { createVtexCommerceLoaders, createCachedPDPLoader } from "@decocms/apps-vtex/commerceLoaders";
import { getAddressByPostalCode } from "@decocms/apps-vtex/loaders/address";
import { createAddressFromRequest, updateAddressFromRequest, deleteAddressFromRequest } from "@decocms/apps-vtex/actions/address";
import { updateProfileFromRequest, newsletterProfileFromRequest, deletePaymentFromRequest, getPasswordLastUpdate } from "@decocms/apps-vtex/actions/profile";
import { createCachedLoader } from "@decocms/blocks/sdk/cachedLoader";
import { createBlogLoaders } from "@decocms/apps-blog";
import { secrets } from "../utils/secrets";
import { onlyAvailableProducts } from "../utils/availability";
import { siteLoaders } from "../../.deco/loaders.gen";

const DOMAIN_RE = /;\s*domain=[^;]*/gi;

export const vtexLoaders = createVtexCommerceLoaders();
export const cachedPDP = createCachedPDPLoader();
export const blogLoaders = createBlogLoaders();

type Loader = (props: any, request?: Request) => Promise<any>;

// Loaders de prateleira (lista de produtos) referenciados pelos blocos do CMS.
// Produto sem nenhum SKU em estoque sai da prateleira mesmo quando o catálogo
// o devolve ("Exibir no site sem estoque" mantém só a PDP no ar). Wishlist
// fica de fora: lá o cliente quer ver o que salvou, esgotado ou não.
const SHELF_LOADER_KEYS = [
  "vtex/loaders/legacy/productList",
  "vtex/loaders/intelligentSearch/productList",
  "vtex/loaders/ProductList",
  "site/loaders/daniela/shelfProductsFull",
  "site/loaders/daniela/productsByCollectionId",
].flatMap((key) => [key, `${key}.ts`]);

function hideOutOfStock(loaders: Record<string, Loader>): Record<string, Loader> {
  const wrapped: Record<string, Loader> = {};
  for (const key of SHELF_LOADER_KEYS) {
    const loader = loaders[key];
    if (loader) wrapped[key] = async (props, request) => onlyAvailableProducts(await loader(props, request));
  }
  return wrapped;
}

const baseLoaders: Record<string, Loader> = {
  ...vtexLoaders,
  ...blogLoaders,
  ...siteLoaders,
};

export const COMMERCE_LOADERS: Record<string, Loader> = {
  ...baseLoaders,
  ...hideOutOfStock(baseLoaders),

  // VTEX address CRUD — request-aware wrappers from @decocms/apps
  "vtex/actions/address/createAddress": createAddressFromRequest as any,
  "vtex/actions/address/updateAddress": updateAddressFromRequest as any,
  "vtex/actions/address/deleteAddress": deleteAddressFromRequest as any,
  "vtex/loaders/address/getAddressByZIP": async (props: any) => {
    return getAddressByPostalCode(props.countryCode, props.postalCode);
  },

  // CMS-referenced site loaders
  "site/loaders/Layouts/ProductCard.tsx": async (props: any) => {
    const mod = await import("../loaders/Layouts/ProductCard");
    return mod.default(props);
  },
  "vtex/loaders/legacy/suggestions.ts": legacySuggestions as any,

  // Profile actions — request-aware wrappers from @decocms/apps
  "vtex/actions/profile/updateProfile": updateProfileFromRequest as any,
  "vtex/actions/profile/updateProfile.ts": updateProfileFromRequest as any,
  "vtex/actions/profile/newsletterProfile": newsletterProfileFromRequest as any,
  "vtex/actions/profile/newsletterProfile.ts": newsletterProfileFromRequest as any,
  "vtex/actions/payments/delete": deletePaymentFromRequest as any,
  "vtex/loaders/profile/passwordLastUpdate": getPasswordLastUpdate as any,
};
